import { useState, useEffect } from "react";
import { getCurrentOrganizationId } from "../api/organizations";
import config from "../config.json";

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
const readGiggedApiScope = config.AuthGigApiReadScope;
const adminDashboardClientId = config.AdminDashboardClientId;
const b2cHostOverride = config.AuthB2cHostOverride;

export type UseLoginRequestReturn = {
    isLoading: boolean
    loginRequest?: LoginRequest
}

export type LoginRequest = {
    scopes: string[]
    extraQueryParameters: {
        organizationId: string
        host: string
        clientId: string
    }
}

export const useLoginRequest = (): UseLoginRequestReturn => {
    const [loginRequest, setLoginRequest] = useState<LoginRequest | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);
    
    useEffect(() => {
        const resolveAuthHost = (): string => {
            const override = (b2cHostOverride || "").trim();
            if (!override) return window.location.hostname;

            try {
                if (override.startsWith("http://") || override.startsWith("https://")) {
                    return new URL(override).hostname;
                }
            } catch {
                // Fall back to non-URL parsing.
            }

            return override.replace(/^https?:\/\//, "").split("/")[0].split(":")[0];
        };

        const fetchData = async () => {
            setIsLoading(true);
            
            const organizationId = await getCurrentOrganizationId();

            setLoginRequest({
                scopes: [readGiggedApiScope],
                extraQueryParameters: { 
                    organizationId: organizationId,
                    host: resolveAuthHost(),
                    clientId: adminDashboardClientId,
                }
            });

            setIsLoading(false);
        };

        fetchData();
    }, []);

    return {
        isLoading: isLoading,
        loginRequest: loginRequest
    };
};
